import React, { useCallback } from "react";

import AddIcon from "@mui/icons-material/Add";
import { Box, Button, Stack, Typography } from "@mui/material";
import { CashflowToolsIcon } from "assets/material-custom-icons";
import { UploadFilesContent } from "components/AiData/UploadFilesContent";
import { DealCreate } from "components/modals";
import { AiProcessingContent } from "components/modals/Files-categorization-modal/AiProcessedContent";
import { useCanCreateDeal, useDevice, useNavigate, usePrompt } from "hooks";
import { useDealActions } from "store";
import pickFiles from "utils/pick-files";

export const EmptyCashflow: React.FC = () => {
  const openPrompt = usePrompt();
  const navigate = useNavigate();
  const { isMobile } = useDevice();
  const canCreateDeal = useCanCreateDeal();
  const { uploadFilesWithAiAction } = useDealActions();

  const createDeal = async () => {
    const data = await openPrompt({ Component: DealCreate, props: {} });

    if (!data) return;

    navigate({ route: "deal", params: { deal_id: data.id } });
  };

  const handleUploadFile = useCallback(async () => {
    const fileList = await pickFiles({
      multiple: true,
      accept: "application/pdf"
    });

    if (!fileList?.length) return;

    const result = await openPrompt({
      Component: UploadFilesContent,
      props: {
        files: fileList
      }
    });

    if (result) {
      try {
        await openPrompt({
          Component: AiProcessingContent,
          props: {
            title: "Upload files",
            type: "aiCategorization",
            applyButtonText: "Apply",
            cb: async () => {
              const promises = Object.entries(result).map(([dealId, files]) => {
                return uploadFilesWithAiAction({
                  dealId: Number(dealId),
                  files: files,
                  aiExtraction: true
                });
              });
              await Promise.all(promises);
            }
          }
        });
      } catch (error) {
        console.error(error);
      }
    }
  }, [openPrompt, uploadFilesWithAiAction]);

  return (
    <Box
      sx={{
        bgcolor: "#f6f8fc",
        border: "1px solid #dbe8f5",
        borderRadius: "8px",
        height: isMobile ? "262px" : "186px",
        display: "flex",
        alignItems: "center",
        ...(isMobile
          ? {
              justifyContent: "center",
              padding: "52px 35px"
            }
          : {
              justifyContent: "center",
              gap: "35px"
            })
      }}
    >
      {isMobile ? (
        <Stack
          direction="column"
          alignItems="center"
          justifyContent="center"
          sx={{
            height: "100%",
            gap: "7px"
          }}
        >
          {/* Mobile: Chart Visualization - Centered */}
          <Box
            sx={{
              height: "70px",
              width: "259px",
              display: "flex",
              justifyContent: "center",
              alignItems: "center"
            }}
          >
            <CashflowToolsIcon sx={{ width: "259px", height: "70px" }} />
          </Box>

          {/* Mobile: Centered Text */}
          <Typography
            sx={{
              fontSize: "20px",
              fontWeight: 400,
              color: "#0a3142",
              lineHeight: "28px",
              whiteSpace: "pre-wrap",
              textAlign: "center",
              width: "259px"
            }}
          >
            Get your own prediction
          </Typography>

          {/* Mobile: Buttons - Side by side */}
          <Stack direction="row" gap="10px" sx={{ mt: "6px" }}>
            {canCreateDeal && (
              <Button
                startIcon={<AddIcon sx={{ fontSize: "20px" }} />}
                variant="contained"
                onClick={createDeal}
                sx={{
                  textTransform: "none",
                  fontSize: "16px",
                  padding: "4px 16px",
                  height: "32px",
                  bgcolor: "#071f35",
                  color: "white",
                  borderRadius: "32px",
                  "&:hover": {
                    bgcolor: "#0a3142"
                  }
                }}
              >
                Add Deal
              </Button>
            )}
            <Button
              startIcon={<AddIcon sx={{ fontSize: "20px" }} />}
              variant="outlined"
              onClick={handleUploadFile}
              sx={{
                textTransform: "none",
                fontSize: "16px",
                padding: "4px 16px",
                height: "32px",
                borderColor: "#7b92a9",
                color: "#071f35",
                borderRadius: "32px",
                "&:hover": {
                  borderColor: "#7b92a9",
                  backgroundColor: "transparent"
                }
              }}
            >
              Upload file
            </Button>
          </Stack>
        </Stack>
      ) : (
        <>
          {/* Desktop: Chart Visualization */}
          <Box
            sx={{
              height: "107px",
              width: "396px",
              display: "flex",
              alignItems: "center"
            }}
          >
            <CashflowToolsIcon sx={{ width: "396px", height: "107px" }} />
          </Box>

          {/* Desktop: Right side content */}
          <Stack height="107px" justifyContent="flex-end">
            <Stack
              sx={{
                gap: "8px",
                alignItems: "flex-start"
              }}
            >
              <Typography
                sx={{
                  fontSize: "20px",
                  fontWeight: 400,
                  color: "#0a3142",
                  lineHeight: "28px",
                  whiteSpace: "pre-wrap"
                }}
              >
                Get your own prediction
              </Typography>
              <Stack direction="row" gap="10px">
                {canCreateDeal && (
                  <Button
                    startIcon={<AddIcon sx={{ fontSize: "20px" }} />}
                    variant="contained"
                    onClick={createDeal}
                    sx={{
                      textTransform: "none",
                      fontSize: "16px",
                      padding: "4px 16px",
                      height: "32px",
                      bgcolor: "#071f35",
                      color: "white",
                      borderRadius: "32px",
                      "&:hover": {
                        bgcolor: "#0a3142"
                      }
                    }}
                  >
                    Add Deal
                  </Button>
                )}
                <Button
                  startIcon={<AddIcon sx={{ fontSize: "20px" }} />}
                  variant="outlined"
                  onClick={handleUploadFile}
                  sx={{
                    textTransform: "none",
                    fontSize: "16px",
                    padding: "4px 16px",
                    height: "32px",
                    borderColor: "#7b92a9",
                    color: "#071f35",
                    borderRadius: "32px",
                    "&:hover": {
                      borderColor: "#7b92a9",
                      backgroundColor: "transparent"
                    }
                  }}
                >
                  Upload file
                </Button>
              </Stack>
            </Stack>
          </Stack>
        </>
      )}
    </Box>
  );
};
